import { cn } from '@/lib/utils';
import {
  BuildingOffice2Icon,
  CalendarIcon,
  CreditCardIcon,
  HeartIcon,
  HomeIcon,
} from '@heroicons/react/24/outline';
import {
  BuildingOffice2Icon as BuildingOffice2SolidIcon,
  CalendarIcon as CalendarSolidIcon,
  CreditCardIcon as CreditCardSolidIcon,
  HeartIcon as HeartSolidIcon,
  HomeIcon as HomeSolidIcon,
} from '@heroicons/react/24/solid';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { hcVerbiage, Language } from '../../common/verbiage';

type Props = {
  basePath: string;
  language: Language;
  // Essentials clinics (`ehr: false`) hide Appts + Balance tabs —
  // those features aren't connected to anything for them.
  hasEhr?: boolean;
};

type NavItem = {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  activeIcon: React.ComponentType<{ className?: string }>;
  /**
   * Routes that should light this tab up. Includes the tab's own URL plus
   * any detail/child routes that live conceptually inside this section
   * but aren't URL descendants (e.g. `/exercises/:id` belongs to Health,
   * `/schedule` and `/thank-you` belong to Appts). Each entry is matched
   * as an exact path or as a path-boundary prefix — see `matchesTab`.
   */
  activePaths: string[];
};

// A tab "owns" a URL if the URL matches one of its activePaths, either
// exactly or as a `/whatever/...` descendant. Raw `startsWith` would
// also match `/healthhub` against `/health`, which we don't want.
const matchesTab = (pathname: string, activePaths: string[]): boolean => {
  return activePaths.some(p => pathname === p || pathname.startsWith(`${p}/`));
};

const BottomNav: React.FC<Props> = ({ basePath, language, hasEhr = true }) => {
  const { pathname } = useLocation();

  const b = basePath || '';

  const navItems: NavItem[] = [
    {
      to: basePath || '/',
      label: hcVerbiage.home[language],
      icon: HomeIcon,
      activeIcon: HomeSolidIcon,
      // Home is the only tab that needs an exact match — everything else
      // would steal the active state from deeper routes.
      activePaths: [basePath || '/'],
    },
    {
      to: `${b}/health`,
      label: hcVerbiage.health[language],
      icon: HeartIcon,
      activeIcon: HeartSolidIcon,
      // Exercises, conditions, and advice all live under the Health
      // hub conceptually even though they have their own top-level URLs.
      activePaths: [`${b}/health`, `${b}/exercises`, `${b}/conditions`, `${b}/advice`],
    },
    ...(hasEhr
      ? [
          {
            to: `${b}/appointments`,
            label: language === 'es' ? 'Citas' : 'Appts',
            icon: CalendarIcon,
            activeIcon: CalendarSolidIcon,
            // /schedule is the "new appointment" flow; /thank-you is its
            // success screen. Both should keep the Appts tab lit.
            activePaths: [`${b}/appointments`, `${b}/schedule`, `${b}/thank-you`],
          },
          // Balance tab. Page-level gating on `allowOnlinePayments`
          // (see BalancePlaceholder) hides Pay / Add-card affordances
          // when the clinic has disabled portal payments; the balance
          // breakdown remains visible per spec.
          {
            to: `${b}/balance`,
            label: hcVerbiage.balance[language],
            icon: CreditCardIcon,
            activeIcon: CreditCardSolidIcon,
            activePaths: [`${b}/balance`],
          },
        ]
      : []),
    {
      to: `${b}/clinic`,
      label: hcVerbiage.clinic[language],
      icon: BuildingOffice2Icon,
      activeIcon: BuildingOffice2SolidIcon,
      activePaths: [`${b}/clinic`],
    },
  ];

  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 bg-white dark:bg-[var(--color-chrome-bg)] z-50 safe-area-bottom shadow-[0_-1px_12px_rgba(0,0,0,0.06)] dark:shadow-none border-t border-[var(--color-chrome-border)]"
      // Right edge sits at the scrollbar's left edge so the
      // scrollbar doesn't overlap the nav. `--scrollbar-w` is
      // measured at app startup (see main.tsx) and resolves to 0
      // on overlay-scrollbar platforms.
      style={{ right: 'var(--scrollbar-w, 0px)' }}
    >
      <div className="flex justify-around">
        {navItems.map(item => {
          const isActive = matchesTab(pathname, item.activePaths);
          const Icon = isActive ? item.activeIcon : item.icon;

          return (
            <Link
              key={item.to}
              to={item.to}
              className={cn(
                'relative flex flex-col items-center pt-2 pb-1.5 px-3 text-[11px] font-medium flex-1 transition-colors',
                isActive ? 'text-[var(--primary-color)]' : 'text-gray-400 dark:text-gray-500'
              )}
            >
              {isActive && (
                <span className="absolute top-0 left-1/2 -translate-x-1/2 h-[3px] w-8 rounded-full bg-[var(--primary-color)]" />
              )}
              <Icon className="h-6 w-6" />
              <span className="mt-0.5 truncate">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;
